import { AI_REVIEW_MAX_BYTES, type DocumentDTO } from "@zplanem/responses"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import { formatBytes } from "../../../lib/format-bytes"
import {
	type DocumentScope,
	useUpdateDocument,
} from "../../hooks/use-documents"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

export function DocumentEditModal({
	propertyId: _propertyId,
	scope,
	document,
	onClose,
}: {
	propertyId: string
	scope: DocumentScope
	document: DocumentDTO
	onClose: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const td = t.projects.documents
	const update = useUpdateDocument(scope)

	const [title, setTitle] = useState(document.title ?? "")
	const [aiReadable, setAiReadable] = useState(document.aiReadable)
	const [saveError, setSaveError] = useState<string | null>(null)

	// Over the AI ceiling — turning the toggle on won't help (ZPL-210).
	const tooLargeForAi =
		document.sizeBytes != null && document.sizeBytes > AI_REVIEW_MAX_BYTES

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	async function handleSave() {
		setSaveError(null)
		try {
			await update.mutateAsync({
				documentId: document.id,
				title,
				aiReadable,
			})
			onClose()
		} catch (err) {
			setSaveError(err instanceof ApiError ? err.message : td.errorLoad)
		}
	}

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="doc-edit-title"
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={td.cancel}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-md rounded-lg border border-border bg-popover text-popover-foreground p-6 shadow-2xl">
				<div className="flex items-start justify-between">
					<h2 id="doc-edit-title" className="text-xl font-bold text-foreground">
						{td.editModalTitle}
					</h2>
					<button
						type="button"
						onClick={onClose}
						aria-label={td.cancel}
						className="text-muted-foreground hover:text-foreground px-2 py-1"
					>
						×
					</button>
				</div>

				<div className="mt-4 space-y-4">
					<div>
						<label
							htmlFor="doc-edit-title-input"
							className="block text-sm font-medium text-foreground mb-1"
						>
							{td.titleLabel}
						</label>
						<input
							id="doc-edit-title-input"
							type="text"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder={td.titlePlaceholder}
							className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
						/>
					</div>

					<div>
						<label className="flex items-center gap-2 cursor-pointer select-none">
							<input
								type="checkbox"
								checked={aiReadable}
								onChange={(e) => setAiReadable(e.target.checked)}
								className="rounded border-border"
							/>
							<span className="text-sm text-foreground">
								{td.aiReadableLabel}
							</span>
						</label>
						{tooLargeForAi ? (
							<p className="mt-1 pl-6 text-xs text-warning">
								{td.aiEditNoticeTooLarge.replace(
									"{size}",
									formatBytes(AI_REVIEW_MAX_BYTES),
								)}
							</p>
						) : null}
					</div>
				</div>

				{saveError ? (
					<div
						role="alert"
						className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
					>
						{saveError}
					</div>
				) : null}

				<div className="mt-5 flex justify-end gap-2">
					<Button variant="ghost" onClick={onClose} disabled={update.isPending}>
						{td.cancel}
					</Button>
					<Button onClick={() => void handleSave()} disabled={update.isPending}>
						{td.save}
					</Button>
				</div>
			</div>
		</div>
	)
}
